import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Recipe } from '@/types/recipe';
import { recipeContextManager, subscribeToRecipeContext } from '../../services/recipeContextService';
import { useConversationStore } from '../../stores/conversationStore';
import { useRecipeStore } from '../../stores/recipeStore';
import { Message } from '../../types/assistant';
import EditablePromptInline from './EditablePromptInline';
import HiddenPromptGenerator from './HiddenPromptGenerator';
import { getRecipeChatResponse } from '../../services/geminiService';
import type { GeminiResponse } from '../../services/api/geminiApiService';
import ContextDetectionService from '../../services/contextDetectionService';
import { ButtonActionHandler } from '../../services/assistant/actions/ButtonActionHandler';
import { AssistantAction } from '../../services/assistant/types/AssistantTypes';
import { useAuth } from '@/contexts/AuthContext';
import { getCurrentUserProfile } from '../../lib/user-service';
import { UpgradeModal } from '@/components/common/UpgradeModal';
import { ErrorModal, useErrorModal } from '@/components/common/ErrorModal';
import { getCostForAction, spendCoins, ChefCoinAction } from '@/services/chefcoinService';
import { writePendingRecipe } from '@/lib/pendingRecipe';
import { safeUrl, sanitizeAssistantOutput } from '@/services/assistant/security';
import ChefCoinIcon from '@/components/ui/ChefCoinIcon';
import ProfileCompletionModal from '@/components/ProfileCompletionModal';
import { ImageGenerationModalService } from '../../services/imageModalService';
import { formatErrorForUser, getErrorSuggestion } from '@/utils/errorUtils';
import type { UserProfile } from '@/types/user';
import { LabPortal } from './LabPortal';
import { LabWindow } from './LabWindow';
import './LabAssistant.css';

interface AssistantWidgetProps {
  currentRecipe?: Recipe | null;
  onOpenStudio?: () => void;
}

// Preload audio files
let openSound: HTMLAudioElement | null = null;
let bubbleSounds: HTMLAudioElement[] = [];
let doneSound: HTMLAudioElement | null = null;

if (typeof window !== 'undefined') {
  openSound = new Audio('/chime.wav');
  doneSound = new Audio('/done.mp3');
  bubbleSounds = [
    new Audio('/bubble (1).mp3'),
    new Audio('/bubble (2).mp3'),
    new Audio('/bubble (3).mp3'),
    new Audio('/bubble (4).mp3'),
  ];
}

const AssistantWidget: React.FC<AssistantWidgetProps> = ({ currentRecipe, onOpenStudio }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [contextualRecipe, setContextualRecipe] = useState<Recipe | null>(null);
  const [recipeStats, setRecipeStats] = useState({ total: 0, favorites: 0 });
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [, setStudioOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const { user, chefcoins_balance, setChefcoinsBalance, isGuest } = useAuth();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [userSubscription, setUserSubscription] = useState<'free' | 'standard' | 'premium' | 'banned'>('free');
  const { showError, modalProps: errorModalProps } = useErrorModal();

  const {
    activeConversationId,
    isLoading,
    createConversation,
    addMessage,
    getActiveConversation,
    updateConversationContext,
    setLoading,
    clearError,
    pendingImageGenerationForRecipeId,
    clearPendingImageGeneration,
  } = useConversationStore();

  const activeConversation = getActiveConversation();
  const messages = useMemo(() => activeConversation?.messages || [], [activeConversation]);

  // Initialize conversation with context-aware greeting
  useEffect(() => {
    if (!activeConversationId || !activeConversation) {
      const conversationId = createConversation();
      const pageContext = ContextDetectionService.detectPageContext(
        contextualRecipe || currentRecipe,
        recipeStats.total,
        recipeStats.favorites
      );
      const contextualGreeting = ContextDetectionService.generateContextualGreeting(pageContext);
      addMessage(conversationId, {
        content: contextualGreeting,
        isUser: false,
        text: contextualGreeting,
        sender: 'assistant',
      });
    }
  }, [activeConversationId, activeConversation, createConversation, addMessage, contextualRecipe, currentRecipe, recipeStats]);

  // Subscribe to recipe context changes
  useEffect(() => {
    let statsUpdateTimer: NodeJS.Timeout;

    const unsubscribe = subscribeToRecipeContext(async (context) => {
      setContextualRecipe(context.currentRecipe);

      clearTimeout(statsUpdateTimer);
      statsUpdateTimer = setTimeout(async () => {
        try {
          const enhancedStats = await recipeContextManager.getEnhancedRecipeStats();
          setRecipeStats(prev => {
            if (prev?.total !== enhancedStats.total || prev?.favorites !== enhancedStats.favorites) {
              return enhancedStats;
            }
            return prev;
          });
        } catch {
          setRecipeStats(recipeContextManager.getRecipeStats());
        }
      }, 500);

      if (activeConversationId && context.currentRecipe) {
        updateConversationContext(activeConversationId, { recipe: context.currentRecipe });
      }
    });

    const initializeStats = async () => {
      setContextualRecipe(recipeContextManager.getCurrentRecipe());
      try {
        setRecipeStats(await recipeContextManager.getEnhancedRecipeStats());
      } catch {
        setRecipeStats(recipeContextManager.getRecipeStats());
      }
    };

    initializeStats();
    return () => {
      clearTimeout(statsUpdateTimer);
      unsubscribe();
    };
  }, [activeConversationId, updateConversationContext]);

  // Handle externally triggered image generation
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const processPendingImageRequest = async () => {
      if (pendingImageGenerationForRecipeId && activeConversationId) {
        setIsOpen(true);
        const recipe = await useRecipeStore.getState().fetchRecipeById(pendingImageGenerationForRecipeId);

        if (recipe) {
          ImageGenerationModalService.openModal(recipe, 'assistant');
          addMessage(activeConversationId, {
            content: `🎨 **I've opened the image generator for "${recipe.title}"!**

The image generation modal is now open where you can:
✨ Generate professional food photos using AI
🎯 See your current recipe image for comparison  
✏️ Customize the prompt if needed
💫 Save the perfect image directly to your recipe

The modal will automatically create a high-quality prompt based on your recipe details.`,
            isUser: false,
            text: "I've opened the image generator for you!",
            sender: 'assistant',
            metadata: { type: 'image-modal-opened' }
          });
        } else {
          addMessage(activeConversationId, {
            content: `Error: Could not find recipe with ID ${pendingImageGenerationForRecipeId} to generate image.`,
            isUser: false,
            text: 'Error finding recipe.',
            sender: 'assistant'
          });
        }
        clearPendingImageGeneration();
        scrollToBottom();
      }
    };
    processPendingImageRequest();
  }, [pendingImageGenerationForRecipeId, activeConversationId, addMessage, clearPendingImageGeneration, scrollToBottom]);

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [scrollToBottom, isOpen]);

  useEffect(() => {
    if (isOpen && !isSending) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isSending, isOpen]);

  // Listen for custom event to open assistant widget
  useEffect(() => {
    const handleOpenWidget = (event?: CustomEvent) => {
      setIsOpen(true);
      if (event?.detail?.prefill) {
        setInputMessage(event.detail.prefill);
      }
    };
    window.addEventListener('openAssistantWidget', handleOpenWidget as EventListener);
    return () => window.removeEventListener('openAssistantWidget', handleOpenWidget as EventListener);
  }, []);

  const playSound = (sound: HTMLAudioElement | null) => {
    sound?.play().catch(() => {});
  };

  const playRandomBubbleSound = () => {
    if (bubbleSounds.length > 0) {
      playSound(bubbleSounds[Math.floor(Math.random() * bubbleSounds.length)]);
    }
  };

  const playDoneSound = () => playSound(doneSound);

  const toggleOpen = () => {
    setIsOpen(prevIsOpen => {
      const newIsOpen = !prevIsOpen;
      if (newIsOpen && openSound) playSound(openSound);
      return newIsOpen;
    });
  };

  const handleSendMessage = async () => {
    const trimmedMessage = inputMessage.trim();
    if (!trimmedMessage || !activeConversationId) return;

    if (isGuest()) {
      setShowProfileModal(true);
      return;
    }

    const userProfile = await getCurrentUserProfile();
    if (!userProfile) {
      addMessage(activeConversationId, { content: "Sorry, I'm having trouble accessing your profile.", isUser: false, text: "Error", sender: 'assistant' });
      return;
    }

    setUserSubscription(userProfile.subscription);

    const lowerMessage = trimmedMessage.toLowerCase();
    let action: ChefCoinAction = 'GENERAL_ADVICE';
    if (lowerMessage.includes('generate image') || lowerMessage.includes('create an image')) {
      action = 'IMAGE_GENERATE';
    } else if (lowerMessage.includes('clean up') || lowerMessage.includes('format this')) {
      action = 'RECIPE_CLEANUP';
    } else if (lowerMessage.includes('create a recipe from this') || lowerMessage.startsWith('import from') || lowerMessage.includes('add this recipe') || lowerMessage.includes('parse this')) {
      action = 'RECIPE_CREATE_FROM_TEXT';
    }

    const cost = getCostForAction(action);

    if (cost > 0 && (chefcoins_balance === null || chefcoins_balance < cost)) {
      setIsUpgradeModalOpen(true);
      return;
    }

    processMessage(trimmedMessage, action, cost, userProfile);
  };

  const processMessage = async (
    message: string,
    action: ChefCoinAction,
    cost: number,
    userProfile: UserProfile
  ) => {
    if (!user || !activeConversationId) return;

    setIsSending(true);
    clearError();
    addMessage(activeConversationId, { content: message, isUser: true, text: message, sender: 'user' });
    setInputMessage('');

    try {
      const activeRecipe = contextualRecipe || currentRecipe;
      setLoading(true);
      const conversationHistory = messages.map(msg => ({ content: msg.content, isUser: msg.isUser }));
      const response: GeminiResponse = await getRecipeChatResponse(message, activeRecipe || undefined, conversationHistory, userProfile);

      let assistantResponseContent = "Sorry, I encountered an issue processing your request.";
      let chefcoinCost = 0;
      let chefcoinFeature: ChefCoinAction | undefined;

      if (response.metadata?.type === 'recipe-creation') {
        chefcoinCost = getCostForAction('RECIPE_CREATE_FROM_TEXT');
        chefcoinFeature = 'RECIPE_CREATE_FROM_TEXT';
      } else if (response.metadata?.type === 'recipe-modify') {
        chefcoinCost = getCostForAction('RECIPE_MODIFY');
        chefcoinFeature = 'RECIPE_MODIFY';
      } else if (response.metadata?.type === 'image-request' || response.metadata?.type === 'clean-image-generation') {
        chefcoinCost = getCostForAction('IMAGE_GENERATE');
        chefcoinFeature = 'IMAGE_GENERATE';
      }

      if (chefcoinCost > 0 && response.metadata?.type !== 'clarify-intent') {
        if (chefcoins_balance !== null && chefcoins_balance < chefcoinCost) {
          setIsUpgradeModalOpen(true);
          setLoading(false);
          setIsSending(false);
          return;
        }
        const spendResult = await spendCoins(user.id, chefcoinCost, { feature: chefcoinFeature });
        if (!spendResult.success) throw new Error(spendResult.error);
        setChefcoinsBalance(spendResult.newBalance);
      }

      if (response.success && response.text) {
        assistantResponseContent = sanitizeAssistantOutput(response.text);
        playRandomBubbleSound();
        if ((response.metadata?.type === 'recipe-creation' || response.metadata?.type === 'recipe-import-confirm') && response.metadata?.formData) {
          writePendingRecipe(response.metadata.formData);
          navigate('/recipe/new/form');
        }
        if (response.metadata?.shouldOpenStudio) {
          setTimeout(() => { setStudioOpen(true); playDoneSound(); }, 500);
        }
      } else if (response.error) {
        const friendly = formatErrorForUser(response.error);
        getErrorSuggestion(response.error);
        showError(friendly, 'Request Failed');
        assistantResponseContent = `I wasn't able to help with that right now. Please try again in a moment.`;
      }

      addMessage(activeConversationId, {
        content: assistantResponseContent,
        isUser: false,
        text: assistantResponseContent,
        sender: 'assistant',
        metadata: { ...response.metadata, chefcoinCost }
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      const friendly = formatErrorForUser(errorMessage);
      showError(friendly, 'Something Went Wrong');
      addMessage(activeConversationId, { content: `I ran into an issue — please try again in a moment.`, isUser: false, text: "Error", sender: 'assistant' });
    } finally {
      setIsSending(false);
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleResetConversation = () => {
    const activeRecipe = contextualRecipe || currentRecipe;
    const conversationId = createConversation(activeRecipe ? { recipe: activeRecipe } : undefined);
    const pageContext = ContextDetectionService.detectPageContext(activeRecipe, recipeStats.total, recipeStats.favorites);
    addMessage(conversationId, {
      content: ContextDetectionService.generateContextualGreeting(pageContext),
      isUser: false,
      text: ContextDetectionService.generateContextualGreeting(pageContext),
      sender: 'assistant',
    });
  };

  // Message rendering
  const renderMessageContent = (message: Message) => {
    const { content, metadata } = message;
    return (
      <>
        {content.split('\n').map((line, lineIndex, array) => (
          <React.Fragment key={lineIndex}>
            {renderLineWithLinksAndImages(line, metadata)}
            {lineIndex < array.length - 1 && <br />}
          </React.Fragment>
        ))}
        {metadata && metadata.chefcoinCost > 0 && (
          <div className="flex items-center gap-1 mt-2 text-xs text-terracotta-700 dark:text-terracotta-300 font-semibold">
            <span>-{metadata.chefcoinCost}</span>
            <ChefCoinIcon className="h-4 w-4" />
            <span>ChefCoin spent</span>
          </div>
        )}
      </>
    );
  };

  const renderLineWithLinksAndImages = (line: string, metadata?: Message['metadata']) => {
    const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
    const imageParts: React.ReactNode[] = [];
    let lastImageIndex = 0;
    let imageMatch;

    while ((imageMatch = imageRegex.exec(line)) !== null) {
      if (imageMatch.index > lastImageIndex) {
        imageParts.push(...renderLineWithLinks(line.substring(lastImageIndex, imageMatch.index), metadata));
      }
      imageParts.push(
        <div key={imageMatch.index} className="my-2">
          <img src={safeUrl(imageMatch[2])} alt={imageMatch[1]} className="max-w-full h-auto rounded-lg border border-gray-300 dark:border-gray-600" style={{ maxHeight: '300px' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>
      );
      lastImageIndex = imageMatch.index + imageMatch[0].length;
    }

    if (lastImageIndex < line.length) {
      imageParts.push(...renderLineWithLinks(line.substring(lastImageIndex), metadata));
    }

    return imageParts.length > 0 ? imageParts : renderLineWithLinks(line, metadata);
  };

  const renderLineWithLinks = (line: string, metadata?: Message['metadata']) => {
    const actionButtonRegex = /\[data-action="([^"]+)"([^\]]*)\]([^[]+)\[\/data-action\]/g;
    const editablePromptRegex = /\[EDITABLE_PROMPT:([^:]+):([^\]]+)\]/g;
    const hiddenPromptRegex = /\[HIDDEN_PROMPT_GENERATOR:([^:]+):([^\]]+)\]/g;
    const imageDisplayRegex = /\[IMAGE_DISPLAY:([^\]]+)\]/g;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const allMatches: Array<{ type: string; match: RegExpExecArray; index: number }> = [];

    let match: RegExpExecArray | null;
    actionButtonRegex.lastIndex = 0;
    while ((match = actionButtonRegex.exec(line)) !== null) allMatches.push({ type: 'button', match, index: match.index });

    editablePromptRegex.lastIndex = 0;
    while ((match = editablePromptRegex.exec(line)) !== null) allMatches.push({ type: 'editable-prompt', match, index: match.index });

    hiddenPromptRegex.lastIndex = 0;
    while ((match = hiddenPromptRegex.exec(line)) !== null) allMatches.push({ type: 'hidden-prompt', match, index: match.index });

    imageDisplayRegex.lastIndex = 0;
    while ((match = imageDisplayRegex.exec(line)) !== null) allMatches.push({ type: 'image-display', match, index: match.index });

    allMatches.sort((a, b) => a.index - b.index);

    for (const item of allMatches) {
      const { type, match } = item;
      if (match.index > lastIndex) parts.push(...processRegularLinks(line.substring(lastIndex, match.index)));

      if (type === 'button') {
        const action = match[1];
        const attributes = match[2];
        const buttonText = match[3];
        const dataAttrs: Record<string, string> = {};
        const attrRegex = /data-([^=]+)="([^"]+)"/g;
        let attrMatch;
        while ((attrMatch = attrRegex.exec(attributes)) !== null) dataAttrs[attrMatch[1]] = decodeURIComponent(attrMatch[2]);

        parts.push(
          <span key={`button-${match.index}`} className="inline-block mr-2 my-1">
            <Button size="sm" variant="default" className="min-w-[120px] h-8 text-xs font-medium bg-gray-600 hover:bg-gray-700 text-white" onClick={() => handleActionButton(action, dataAttrs)} disabled={isLoading}>{buttonText}</Button>
          </span>
        );
      } else if (type === 'editable-prompt') {
        const recipeId = match[1];
        const base64Prompt = match[2];
        try {
          const decodedPrompt = atob(base64Prompt);
          const handleGenerate = async (_prompt: string) => {
            if (!activeConversationId) return;
            try {
              const recipe = await useRecipeStore.getState().fetchRecipeById(recipeId);
              if (recipe) {
                ImageGenerationModalService.openModal(recipe, 'assistant');
                addMessage(activeConversationId, { content: `🎨 **I've opened the image generator with your custom prompt!**\n\nYour edited prompt is ready in the image generation modal.`, isUser: false, text: 'Opened image generator!', sender: 'assistant', metadata: { type: 'image-modal-opened' } });
              }
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Unknown error';
              addMessage(activeConversationId, { content: `❌ Image generation failed: ${formatErrorForUser(errorMessage)}`, isUser: false, text: `Failed: ${errorMessage}`, sender: 'assistant' });
            }
          };
          parts.push(<div key={`editable-${match.index}`}><EditablePromptInline initialPrompt={decodedPrompt} recipeId={recipeId} enhancedText={metadata?.enhancedDescription as string | undefined} onGenerate={handleGenerate} isGenerating={isLoading ?? false} /></div>);
        } catch {
          parts.push(<div key={`error-${match.index}`} className="text-red-500 text-sm">Error: Failed to decode prompt</div>);
        }
      } else if (type === 'hidden-prompt') {
        const recipeId = match[1];
        const base64Prompt = match[2];
        try {
          const decodedPrompt = atob(base64Prompt);
          const handleGenerate = async (_prompt: string) => {
            if (!activeConversationId) return;
            try {
              const recipe = await useRecipeStore.getState().fetchRecipeById(recipeId);
              if (recipe) {
                ImageGenerationModalService.openModal(recipe, 'assistant');
                addMessage(activeConversationId, { content: `🎨 **I've opened the image generator for you!**\n\nThe modal is now open.`, isUser: false, text: 'Opened image generator!', sender: 'assistant', metadata: { type: 'image-modal-opened' } });
              }
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Unknown error';
              addMessage(activeConversationId, { content: `❌ Image generation failed: ${formatErrorForUser(errorMessage)}`, isUser: false, text: `Failed: ${errorMessage}`, sender: 'assistant' });
            }
          };
          parts.push(<div key={`hidden-${match.index}`}><HiddenPromptGenerator initialPrompt={decodedPrompt} recipeId={recipeId} onGenerate={handleGenerate} isGenerating={isLoading ?? false} /></div>);
        } catch {
          parts.push(<div key={`error-hidden-${match.index}`} className="text-red-500 text-sm">Error: Failed to decode prompt</div>);
        }
      } else if (type === 'image-display') {
        parts.push(<div key={`image-${match.index}`} className="my-2"><img src={safeUrl(match[1])} alt="Generated" className="max-w-full h-auto rounded-lg border border-gray-300 dark:border-gray-600" style={{ maxHeight: '300px' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} /></div>);
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) parts.push(...processRegularLinks(line.substring(lastIndex)));
    return parts.length > 0 ? parts : processRegularLinks(line);
  };

  const processRegularLinks = (text: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    let match: RegExpExecArray | null;
    while ((match = linkRegex.exec(text)) !== null) {
      const m = match;
      if (m.index > lastIndex) parts.push(text.substring(lastIndex, m.index));
      parts.push(<a key={m.index} href={safeUrl(m[2])} className="text-terracotta-500 hover:text-terracotta-600 dark:text-terracotta-400 dark:hover:text-terracotta-300 underline decoration-2 underline-offset-2 cursor-pointer font-medium" onClick={(e) => { e.preventDefault(); window.location.href = safeUrl(m[2]); }}>{m[1]}</a>);
      lastIndex = m.index + m[0].length;
    }

    if (lastIndex < text.length) parts.push(text.substring(lastIndex));
    return parts.length > 0 ? parts : [text];
  };

  const handleActionButton = async (action: string, dataAttrs: Record<string, string>) => {
    if (!activeConversationId) return;
    try {
      setLoading(true);
      if (!ButtonActionHandler.isValidAction(action)) throw new Error(`Invalid action: ${action}`);

      const context = { userMessage: `Action: ${action}`, currentRecipe: contextualRecipe || undefined, conversationHistory: messages as any, activeConversationId };
      const uiCallbacks = { addMessage: (cid: string, msg: any) => addMessage(cid, msg), playDoneSound: () => playDoneSound(), onOpenStudio, scrollToBottom };

      await ButtonActionHandler.executeAction(action as AssistantAction, dataAttrs, context, uiCallbacks);
    } catch (error: any) {
      addMessage(activeConversationId, { content: `❌ Error: ${error.message || 'An error occurred'}`, isUser: false, text: 'Error', sender: 'assistant' });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (action: 'paste-recipe' | 'create-recipe') => {
    if (!activeConversationId) return;
    if (isGuest()) {
      setShowProfileModal(true);
      return;
    }

    const actionMessages: Record<string, { user: string; assistant: string }> = {
      'paste-recipe': {
        user: 'I want to paste a recipe',
        assistant: `Great! You have a few options:

**1. Paste it right here** — Just copy & paste the recipe text (from any website, blog, or document) directly into the chat below. I'll parse it and format it for you.

**2. Open the recipe form** — If you prefer to fill in the details yourself, I can open the recipe creation form for you.

What would you like to do?`
      },
      'create-recipe': {
        user: 'I want to create a recipe',
        assistant: `Perfect! I can help you create a recipe in two ways:

**1. Describe it to me** — Tell me what you want to make (e.g., "a quick pasta dish with tomatoes and basil") and I'll generate a full recipe for you.

**2. Open the Recipe Studio** — For more control, I can open the full recipe creation studio where you can choose a chef persona, set dietary preferences, and guide the AI step by step.

What sounds good?`
      }
    };

    const msg = actionMessages[action];
    addMessage(activeConversationId, { content: msg.user, isUser: true, text: msg.user, sender: 'user' });
    playRandomBubbleSound();
    setLoading(true);

    // Simulate thinking delay
    await new Promise(resolve => setTimeout(resolve, 800));

    addMessage(activeConversationId, {
      content: msg.assistant,
      isUser: false,
      text: msg.assistant,
      sender: 'assistant',
      metadata: { type: 'quick-action-response' }
    });
    setLoading(false);
    playDoneSound();
  };

  // Get contextual message for the portal
  const getContextualMessage = (): string | undefined => {
    if (contextualRecipe) return `Working on ${contextualRecipe.title}?`;
    if (recipeStats.total === 0) return 'Start your recipe collection!';
    if (recipeStats.total < 5) return 'Need recipe inspiration?';
    return undefined;
  };

  // Closed state - Lab Portal
  if (!isOpen) {
    return (
      <>
        <ErrorModal {...errorModalProps} />
        <UpgradeModal isOpen={isUpgradeModalOpen} onOpenChange={setIsUpgradeModalOpen} featureName="ChefLab Assistant" subscription={userSubscription} />
        <ProfileCompletionModal isOpen={showProfileCompletion} onComplete={() => setShowProfileCompletion(false)} />
        <LabPortal onOpen={toggleOpen} contextualMessage={getContextualMessage()} recipeTitle={contextualRecipe?.title} isProcessing={isLoading} />
      </>
    );
  }

  // Open state - Lab Window
  return (
    <>
      <ErrorModal {...errorModalProps} />
      <UpgradeModal isOpen={isUpgradeModalOpen} onOpenChange={setIsUpgradeModalOpen} featureName="ChefLab Assistant" subscription={userSubscription} />

      {showProfileModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowProfileModal(false)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 border border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-terracotta-500 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Profile Required</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Please complete your profile to use the AI assistant.</p>
              <div className="flex flex-col gap-2">
                <button className="w-full py-2.5 px-4 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium hover:shadow-lg transition-all" onClick={() => { setShowProfileModal(false); setShowProfileCompletion(true); }}>Complete Profile</button>
                <button className="w-full py-2.5 px-4 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-all" onClick={() => setShowProfileModal(false)}>Maybe Later</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ProfileCompletionModal isOpen={showProfileCompletion} onComplete={() => setShowProfileCompletion(false)} />

      {!showProfileCompletion && (
        <LabWindow
          messages={messages}
          inputMessage={inputMessage}
          isLoading={isLoading}
          isSending={isSending}
          contextualRecipe={contextualRecipe}
          onInputChange={(value) => { setInputMessage(value); }}
          onSendMessage={handleSendMessage}
          onKeyPress={handleKeyPress}
          onReset={handleResetConversation}
          onClose={() => setIsOpen(false)}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = `${Math.min(Math.max(target.scrollHeight, 42), 120)}px`;
          }}
          inputRef={inputRef}
          messagesEndRef={messagesEndRef}
          renderMessageContent={renderMessageContent}
          onQuickAction={handleQuickAction}
        />
      )}
    </>
  );
};

export default AssistantWidget;
